import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateWarranty } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";
import moment from "moment";
import { InputSelect } from "components/Form/InputSelect";
import { getRole } from "hooks";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  warrantyId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  isApproved: "",
  warrantyExpiry: "",
};

const WarrantyFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  warrantyId,
}) => {
  const isAddMode = !warrantyId;
  const dispatch = useDispatch();
  const userRole = getRole();
  const formikRef = useRef<FormikValues>() as any;

  const [isApproved, setIsApproved] = useState<ValueSchema | null>(null);
  const [tenant, setTenant] = useState<ValueSchema | null>(null);

  const { warranties, isFormLoading } = useSelector(
    (state: any) => state.warrantyReducer
  );

  const [isApprovedItems] = React.useState([
    { label: "No", value: 0 },
    { label: "Yes", value: 1 },
  ]);
  const [tenantItems] = React.useState([
   { label: "Ancol Points", value: 3 },
  ]);

  const handleCloseModal = () => {
    onClose && onClose();
    setIsApproved(null);
    setTenant(null);
  };

  const handleSubmit = (values: any) => {
    const { isApproved, warrantyExpiry, tenantId } = values;

    dispatch(
      updateWarranty(
        warrantyId,
        { isApproved, warrantyExpiry, tenantId },
        handleCloseModal
      ) as any
    );
  };

  useEffect(() => {
    if (!isAddMode) {
      const warrantyDetail = warranties.find(
        (warranty: any) => warranty.id === parseInt(warrantyId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue(
          "isApproved",
          warrantyDetail.is_approved
        );
        formikRef.current.setFieldValue(
          "warrantyExpiry",
          moment(warrantyDetail.warranty_expiry).format("YYYY-MM-DD")
        );
        formikRef.current.setFieldValue("tenantId", warrantyDetail.tenant_id);

        isApprovedItems.map((item) =>
          item.value.toString() === warrantyDetail.is_approved
            ? setIsApproved(item)
            : null
        );

        tenantItems.map((item) =>
          item.value.toString() === warrantyDetail.tenant_id
            ? setTenant(item)
            : null
        );
      }
    }
  }, [isAddMode, warranties, warrantyId]);

  const validationSchema = yup.object({
    isApproved: yup.string().required("Redirect Url is required field"),
    warrantyExpiry: yup.date().required("Warranty Expiry is required field"),
    tenantId:
      userRole !== "tenant"
        ? yup.string().required("Tenant is required field")
        : yup.string(),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text>Update Warranty</Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="isApproved">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="isApproved">Approved</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputSelect
                            selectedItem={isApproved}
                            width={"100%"}
                            placeholder="Select Approved"
                            handleSelectedItemChange={({ selectedItem }) => {
                              if (selectedItem) {
                                setIsApproved(selectedItem);
                                setFieldValue("isApproved", selectedItem.value);
                              }
                            }}
                            itemToString={(item) =>
                              item ? `${item.label}` : ""
                            }
                            itemValue={(item) => (item ? `${item.value}` : "")}
                            items={isApprovedItems}
                            size={"lg"}
                            borderRadius={"6px"}
                            errors={!!meta.error && meta.touched}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="warrantyExpiry">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="warrantyExpiry">
                          Warranty Expiry
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="date"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  {userRole !== "tenant" ? (
                    <Field name="tenantId">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="tenantId">Tenant</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={tenant}
                              width={"100%"}
                              placeholder="Select Tenant"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setTenant(selectedItem);
                                  setFieldValue("tenantId", selectedItem.value);
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={tenantItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  ) : (
                    <></>
                  )}
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    Update
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default WarrantyFormModal;
